import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Images } from '../../utils/imageConstants';
import PersonalDetailsStep from './steps/PersonalDetailsStep';
import PlayerDetailsStep from './steps/PlayerDetailsStep';
import UmpireDetailsStep from './steps/UmpireDetailsStep';
import ScorerDetailsStep from './steps/ScorerDetailsStep';
import OtherInfoStep from './steps/OtherInfoStep';
import { updatePlayerProfileV2 } from './api/profileOnboardingApi';
import { isSafeRedirectPath } from '../../helper/helper';
import { buildPlayerV2Payload, type ProfileOnboardingDraft } from './mappers/buildPlayerV2Payload';
import type { ProfileOnboardingLocationState, ProfileStepId } from './types/profileOnboarding.d';
import type { PersonalDetailsFormValues } from './validation/personalDetailsSchema';
import type { PlayerDetailsFormValues } from './validation/playerDetailsSchema';
import type { UmpireDetailsFormValues } from './validation/umpireDetailsSchema';
import type { ScorerDetailsFormValues } from './validation/scorerDetailsSchema';
import type { OtherInfoFormValues } from './validation/otherInfoSchema';
import './CompleteProfile.scss';

interface StepConfig {
  id: ProfileStepId;
  label: string;
}

function buildSteps(isPlayer: boolean, isUmpire: boolean, isScorer: boolean): StepConfig[] {
  const steps: StepConfig[] = [{ id: 'personal', label: 'Personal Details' }];
  if (isPlayer) steps.push({ id: 'player', label: 'Player Details' });
  if (isUmpire) steps.push({ id: 'umpire', label: 'Umpire Details' });
  if (isScorer) steps.push({ id: 'scorer', label: 'Scorer Details' });
  steps.push({ id: 'other', label: 'Other Info' });
  return steps;
}

function CompleteProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [locationState, setLocationState] = useState<ProfileOnboardingLocationState | null>(null);
  const [draft, setDraft] = useState<ProfileOnboardingDraft>({});
  const [previewRoles, setPreviewRoles] = useState<{
    isPlayer: boolean;
    isUmpire: boolean;
    isScorer: boolean;
  } | null>(null);
  const [partialSaving, setPartialSaving] = useState(false);

  const steps = useMemo(() => {
    const r = draft.personal
      ? {
        isPlayer: draft.personal.isPlayer,
        isUmpire: draft.personal.isUmpire,
        isScorer: draft.personal.isScorer,
      }
      : previewRoles ?? { isPlayer: false, isUmpire: false, isScorer: false };
    return buildSteps(r.isPlayer, r.isUmpire, r.isScorer);
  }, [draft.personal, previewRoles]);

  const currentStepId = steps[currentStepIndex]?.id ?? 'personal';

  useEffect(() => {
    const state = location.state as ProfileOnboardingLocationState | undefined;
    if (!state?.email || !state?.userId) {
      navigate('/', { replace: true });
      return;
    }
    setLocationState(state);
    if (state.firstName || state.lastName) {
      setDraft((d) =>
        d.personal
          ? d
          : {
            ...d,
            personal: {
              firstName: state.firstName ?? '',
              middleName: '',
              lastName: state.lastName ?? '',
              dateOfBirth: '',
              gender: '' as PersonalDetailsFormValues['gender'],
              isPlayer: false,
              isUmpire: false,
              isScorer: false,
            },
          }
      );
    }
  }, [location.state, navigate]);

  const goToNextStep = useCallback(() => {
    setCurrentStepIndex((i) => i + 1);
  }, []);

  const handleStep1Next = useCallback(
    (data: PersonalDetailsFormValues, profileImage: File | null) => {
      setDraft((d) => ({
        ...d,
        personal: { ...data, profileImage: profileImage ?? undefined },
      }));
      setCurrentStepIndex(1);
    },
    []
  );

  const handlePlayerNext = useCallback(
    (data: PlayerDetailsFormValues) => {
      setDraft((d) => ({ ...d, player: data }));
      goToNextStep();
    },
    [goToNextStep]
  );

  const handleUmpireNext = useCallback(
    (data: UmpireDetailsFormValues) => {
      setDraft((d) => ({ ...d, umpire: data }));
      goToNextStep();
    },
    [goToNextStep]
  );

  const handleScorerNext = useCallback(
    (data: ScorerDetailsFormValues) => {
      setDraft((d) => ({ ...d, scorer: data }));
      goToNextStep();
    },
    [goToNextStep]
  );

  const handlePartialSave = useCallback(async () => {
    if (!locationState) return;
    if (!draft.personal) {
      toast.error('Complete personal details first.');
      return;
    }
    setPartialSaving(true);
    try {
      const body = buildPlayerV2Payload(locationState, draft, 'PARTIALLY_UPDATED');
      await updatePlayerProfileV2(body);
      toast.success('Progress saved.');
      navigate(isSafeRedirectPath(locationState.redirectTo) ? locationState.redirectTo : '/', { replace: true });
    } catch {
      toast.error('Could not save profile. Please try again.');
    } finally {
      setPartialSaving(false);
    }
  }, [draft, locationState, navigate]);

  const handleOtherComplete = useCallback(
    async (values: OtherInfoFormValues) => {
      if (!locationState) return;
      if (!draft.personal) {
        toast.error('Missing personal details.');
        return;
      }
      const mergedDraft: ProfileOnboardingDraft = { ...draft, other: values };
      try {
        const body = buildPlayerV2Payload(locationState, mergedDraft, 'COMPLETED');
        await updatePlayerProfileV2(body);
        setDraft(mergedDraft);
        toast.success('Welcome to CricCenter!');
        navigate(isSafeRedirectPath(locationState.redirectTo) ? locationState.redirectTo : '/', { replace: true });
      } catch {
        toast.error('Failed to complete profile. Please try again.');
      }
    },
    [draft, locationState, navigate]
  );

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex((i) => i - 1);
    }
  };

  const showBackArrow = currentStepIndex > 0;
  const isStepCompleted = (index: number) => index < currentStepIndex;

  if (!locationState) {
    return null;
  }

  return (
    <div className="complete-profile-wrapper">
      <div className="complete-profile-background">
        <div className="logo-container">
          <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
          <h1 className="app-name">CRICCENTER</h1>
        </div>
        <div className="complete-profile-card">
          <div className="complete-profile-header">
            {showBackArrow && (
              <button
                type="button"
                className="complete-profile-back-btn"
                onClick={handleBack}
                aria-label="Go back to previous step"
              >
                <img src={Images.previous_icon} alt="" />
                <span>Back</span>
              </button>
            )}


          </div>

          <ol className="complete-profile-stepper" role="list">
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`complete-profile-step ${currentStepIndex === index ? 'active' : ''} ${isStepCompleted(index) ? 'completed' : ''}`}
              >
                {index > 0 && (
                  <span
                    className={`complete-profile-step-connector ${currentStepIndex >= index || isStepCompleted(index - 1) ? 'dark' : ''}`}
                    aria-hidden
                  />
                )}
                <span className="complete-profile-step-dot">
                  {isStepCompleted(index) ? (
                    <span className="check">✓</span>
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="complete-profile-step-label">{step.label}</span>
              </li>
            ))}
          </ol>

          <div className="complete-profile-content">
            {currentStepId === 'personal' && (
              <PersonalDetailsStep
                locationState={locationState}
                initialPersonal={draft.personal}
                onNext={handleStep1Next}
                onRolesChange={(isPlayer, isUmpire, isScorer) =>
                  setPreviewRoles({ isPlayer, isUmpire, isScorer })
                }
              />
            )}
            {currentStepId === 'player' && (
              <PlayerDetailsStep
                initialValues={draft.player}
                onNext={handlePlayerNext}
                onCompleteLater={handlePartialSave}
                partialSaving={partialSaving}
              />
            )}
            {currentStepId === 'umpire' && (
              <UmpireDetailsStep
                initialValues={draft.umpire}
                onNext={handleUmpireNext}
                onCompleteLater={handlePartialSave}
                partialSaving={partialSaving}
              />
            )}
            {currentStepId === 'scorer' && (
              <ScorerDetailsStep
                initialValues={draft.scorer}
                onNext={handleScorerNext}
                onCompleteLater={handlePartialSave}
                partialSaving={partialSaving}
              />
            )}
            {currentStepId === 'other' && (
              <OtherInfoStep initialValues={draft.other} onComplete={handleOtherComplete} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompleteProfile;
